import {
  DraftDataDto,
  FormBasicsDto,
  FormChildQuestionDto,
  FormQuestionDto,
  FormSectionDto,
  QuestionComponentType,
  QuestionDataType,
} from "@green0meter/api-client";
import { debounce } from "lodash";
import React, { FC, Fragment, useCallback, useEffect, useState } from "react";
import { FieldValues, useFormContext, useWatch } from "react-hook-form";

import { useCreateUpdateDraftData, useGetFormDraftData } from "@api";
import { useUserStore } from "@store/user";

interface DraftAutosave {
  formBasics: FormBasicsDto;
  section: FormSectionDto;
  question: FormQuestionDto | FormChildQuestionDto;
  parentQuestionCode?: string;
  rowIndex?: number;
}

const DraftAutosave: FC<DraftAutosave> = ({
  formBasics,
  section,
  question,
  parentQuestionCode,
  rowIndex,
}) => {
  const [rowIndexes, setRowIndexes] = useState<number[]>([]);
  const { user } = useUserStore();
  const {
    createUpdateDraftData,
    createUpdateDraftDataEnabled,
    isPendingCreateUpdateDraftData,
  } = useCreateUpdateDraftData(user, formBasics.id);
  const { formDraftData, isPendingFormDraftData } = useGetFormDraftData(
    user,
    formBasics.id,
  );
  const { control } = useFormContext();
  const inputName = rowIndex
    ? `${parentQuestionCode}-${question.code}-${rowIndex}`
    : parentQuestionCode
      ? `${parentQuestionCode}-${question.code}`
      : question.code;
  const currentValue = useWatch<FieldValues, string>({
    control,
    name: inputName,
    defaultValue: "",
  });
  // TODO: Not very good solution, but needed to get the relevant year value
  const relevantYearValue = useWatch<FieldValues, string>({
    control,
    name: `${question.code}-relevantYear`,
    defaultValue: "",
  });

  const saveDraft = (
    value: string,
    relevantYear?: number,
    parentQuestionCode?: string,
  ): void => {
    if (!formBasics.isLocked) {
      const newValue =
        question.componentType === QuestionComponentType.Slider
          ? value
          : question.dataType === QuestionDataType.Number
            ? parseFloat((value as string).replace(",", "."))
            : value;

      createUpdateDraftData({
        formId: formBasics.id,
        questionCode: question.code,
        rowIndex,
        // @ts-expect-error TODO: Fix this on BE
        value: newValue,
        relevantYear,
        parentQuestionCode,
      });
    }
  };

  const debounceSaveTimeout =
    question.componentType === QuestionComponentType.Radio ||
    question.componentType === QuestionComponentType.Select
      ? 0
      : 750;

  const debounceSaveDraft = useCallback(
    debounce(saveDraft, debounceSaveTimeout),
    [],
  );

  const getValue = (draftElement?: DraftDataDto): string => {
    if (!draftElement) {
      return "";
    }

    if (
      question.componentType === QuestionComponentType.Radio ||
      question.componentType === QuestionComponentType.Checkbox ||
      question.componentType === QuestionComponentType.Select
    ) {
      return String(draftElement.answerOptionCode || "");
    }

    return String(draftElement.value || "");
  };

  useEffect(() => {
    const questionCode = question.code;

    const draft = formDraftData?.find((s) => {
      if (rowIndex) {
        return (
          s.questionCode === questionCode &&
          s.parentQuestionCode === parentQuestionCode &&
          s.rowIndex === rowIndex
        );
      }

      if (parentQuestionCode) {
        return (
          s.questionCode === questionCode &&
          s.parentQuestionCode === parentQuestionCode
        );
      }

      return s.questionCode === questionCode;
    });
    const draftValue = getValue(draft);
    const draftRelevantYear = draft?.relevantYear || "";

    if (
      createUpdateDraftDataEnabled &&
      !isPendingFormDraftData &&
      !isPendingCreateUpdateDraftData &&
      question.componentType !== QuestionComponentType.Files
    ) {
      if (
        draftValue !== String(currentValue ?? "") ||
        String(draftRelevantYear) !== String(relevantYearValue ?? "")
      ) {
        debounceSaveDraft(currentValue, relevantYearValue, parentQuestionCode);
      }
    }
  }, [currentValue, relevantYearValue]);

  // Table row indexes
  useEffect(() => {
    const q = question as FormQuestionDto;

    if (
      formDraftData &&
      question.componentType === QuestionComponentType.Table &&
      q.childQuestions
    ) {
      const newRowIndexes: number[] = [];
      const childQuestionCodes = q.childQuestions.map((q) => q.code);

      formDraftData
        .filter((draftData) => childQuestionCodes.includes(draftData.questionCode))
        .forEach((draftData) => {
          if (!newRowIndexes.includes(draftData.rowIndex)) {
            newRowIndexes.push(draftData.rowIndex);
          }
        });

      setRowIndexes(newRowIndexes);
    }
  }, [formDraftData]);

  return question.componentType === QuestionComponentType.MultiInput
    ? (question as FormQuestionDto).childQuestions?.map((childQuestion) => (
        // Child questions autosave
        <DraftAutosave
          key={`${question.code}-${childQuestion.questionId}`}
          formBasics={formBasics}
          section={section}
          question={childQuestion}
          parentQuestionCode={question.code}
        />
      ))
    : question.componentType === QuestionComponentType.Table
      ? rowIndexes.map((rowIndex) => (
          <Fragment key={`${question.questionId}-${rowIndex}`}>
            {(question as FormQuestionDto).childQuestions?.map((childQuestion) => (
              // Table rows autosave
              <DraftAutosave
                key={`${question.code}-${childQuestion.questionId}-${rowIndex}`}
                formBasics={formBasics}
                section={section}
                question={childQuestion}
                rowIndex={rowIndex}
                parentQuestionCode={question.code}
              />
            ))}
          </Fragment>
        ))
      : null;
};

export default DraftAutosave;
